import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Select, SelectOption } from '@/components/ui/Select'
import {
  useListCampaigns,
  useGetNotificationPhones,
  useGetSeedingContact,
} from '@/api/generated/v2-seeding-campaigns/v2-seeding-campaigns'
import type {
  SeedingCampaignListResponse as SeedingCampaignListResponse,
  NotificationPhonesResponse,
  SeedingContactResponse,
} from '@/api/schemas'
import { auth } from '@/lib/auth'
import { SeedingSettingsModal } from '@/features/seeding/SeedingSettingsModal'
import type { SeedingCampaignResponse } from '@/types'
/**
 * 체험단 캠페인 관리 페이지 (고객사용)
 *
 * 고객사가 자신의 체험단 캠페인을 조회/생성/관리할 수 있는 페이지입니다.
 */
import {
  Link,
  Outlet,
  createFileRoute,
  useLocation,
  useNavigate,
} from '@tanstack/react-router'
import { Gift, Settings } from 'lucide-react'
import { useEffect, useState } from 'react'

export const Route = createFileRoute('/_dashboard/seeding-campaigns')({
  component: SeedingCampaigns,
})

// 상태별 배지 색상
const statusBadgeVariant: Record<
  string,
  'secondary' | 'success' | 'warning' | 'danger' | 'destructive' | 'info' | 'muted' | 'purple'
> = {
  draft: 'secondary',
  scheduled: 'purple',
  recruiting: 'info',
  pending_selection: 'warning',
  in_progress: 'success',
  completed: 'muted',
  archived: 'destructive',
}

// 상태 한글 변환
const statusLabel: Record<string, string> = {
  draft: '작성중',
  scheduled: '모집예정',
  recruiting: '모집중',
  pending_selection: '선정 대기',
  in_progress: '진행중',
  completed: '종료',
  archived: '아카이브',
}

function SeedingCampaigns() {
  const location = useLocation()
  const navigate = useNavigate()
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('')
  const [showSettingsModal, setShowSettingsModal] = useState(false)

  // 목록 페이지 여부 — Outlet 렌더링 판단
  const isListPage = location.pathname === '/seeding-campaigns'

  // shop 로딩/인증은 레이아웃(_dashboard.tsx)이 이미 보장하므로 여기서 다시 조회하지 않는다.
  // (이 페이지는 shop 필드를 직접 쓰지 않는다)

  // --- Orval Query 훅: 캠페인 목록 조회 (목록 페이지일 때만) ---
  const { data: campaignsRaw, isLoading: campaignsLoading, isError: campaignsError } = useListCampaigns(
    { limit: 100, include_archived: true },
    { query: { enabled: auth.isAuthenticated() && isListPage } },
  )
  const campaignList = (campaignsRaw ?? null) as SeedingCampaignListResponse | null
  // Orval 타입의 필드가 optional이므로 @/types의 SeedingCampaignResponse로 캐스팅
  const campaigns = (campaignList?.campaigns ?? []) as unknown as SeedingCampaignResponse[]

  // --- Orval Query 훅: 알림톡 전화번호 조회 (목록 페이지 + 캠페인 있을 때) ---
  const { data: phonesRaw } = useGetNotificationPhones({
    query: { enabled: auth.isAuthenticated() && isListPage && campaigns.length > 0 },
  })
  const notificationPhones = phonesRaw
    ? ((phonesRaw as unknown as NotificationPhonesResponse)?.phones ?? null)
    : null

  // --- Orval Query 훅: 참여자 문의처 조회 (연락처 설정 배너 판정용) ---
  const { data: contactRaw } = useGetSeedingContact({
    query: { enabled: auth.isAuthenticated() && isListPage && campaigns.length > 0 },
  })
  const seedingContact = contactRaw
    ? (contactRaw as unknown as SeedingContactResponse)
    : null

  // 연락처 설정(담당자 알림 전화번호 + 참여자 문의처) 미완료 배너 판정.
  // 공개 게이트가 둘 다 요구하므로, 하나라도 없으면 안내한다.
  const hasNotificationPhone =
    notificationPhones !== null && notificationPhones.length > 0
  const hasSeedingContact = !!(
    seedingContact &&
    (seedingContact.phone || seedingContact.kakao_url || seedingContact.email)
  )
  // 두 조회가 모두 로드됐고(로딩 중 깜빡임 방지) 하나라도 미등록일 때만 표시
  const showContactBanner =
    campaigns.length > 0 &&
    notificationPhones !== null &&
    contactRaw !== undefined &&
    (!hasNotificationPhone || !hasSeedingContact)
  const contactBannerMessage =
    !hasNotificationPhone && !hasSeedingContact
      ? '담당자 알림 전화번호와 참여자 문의처가 등록되지 않았어요. 등록해야 캠페인을 공개할 수 있어요.'
      : !hasNotificationPhone
        ? '담당자 알림 전화번호가 등록되지 않았어요. 등록하면 신청/선정/미션 알림을 받고 캠페인을 공개할 수 있어요.'
        : '참여자 문의처가 등록되지 않았어요. 참여자가 문의할 수 있도록 등록해야 캠페인을 공개할 수 있어요.'

  const isLoading = isListPage && campaignsLoading

  // 인증 확인
  useEffect(() => {
    if (!auth.isAuthenticated()) {
      navigate({ to: '/' })
    }
  }, [navigate])

  // 검색/필터 적용
  const filteredCampaigns = (() => {
    let filtered = campaigns

    // 검색어 필터
    if (searchTerm) {
      const term = searchTerm.toLowerCase()
      filtered = filtered.filter(
        (c) =>
          c.title.toLowerCase().includes(term) ||
          // 상품명은 임시 저장 캠페인에서 비어 있을 수 있다
          (c.product_name ?? '').toLowerCase().includes(term),
      )
    }

    // 상태 필터 (기본적으로 archived 제외)
    if (statusFilter) {
      filtered = filtered.filter((c) => c.status === statusFilter)
    } else {
      filtered = filtered.filter((c) => c.status !== 'archived')
    }

    return filtered
  })()

  // URL이 상세/생성 페이지인 경우 Outlet 렌더링
  if (!isListPage) {
    return <Outlet />
  }

  // 로딩 중
  if (isLoading) {
    return (
      <div className="max-w-5xl mx-auto animate-pulse">
        <div className="h-8 bg-surface-high rounded w-64 mb-6"></div>
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-surface-high h-24 rounded-lg"></div>
          ))}
        </div>
      </div>
    )
  }

  // 에러 — 캠페인 목록 조회 실패
  if (campaignsError) {
    return (
      <div className="max-w-md mx-auto text-center py-20">
        <h2 className="text-lg font-semibold text-on-surface mb-2">
          오류가 발생했습니다
        </h2>
        <p className="text-sm text-on-surface-variant mb-6">데이터를 불러오는데 실패했어요.</p>
        <Button
          onClick={() => navigate({ to: '/' })}
          variant="outline"
        >
          대시보드로 돌아가기
        </Button>
      </div>
    )
  }

  return (
    <>
      <div className="max-w-5xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h1 className="text-xl font-bold text-on-surface">체험단 관리</h1>
            <p className="mt-1 text-sm text-on-surface-variant">
              총 {campaigns.length}개 캠페인 &bull; 모집중{' '}
              {campaigns.filter((c) => c.status === 'recruiting').length}개
              &bull; 종료{' '}
              {campaigns.filter((c) => c.status === 'completed').length}개
            </p>
          </div>
          <div className="flex gap-2 shrink-0">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowSettingsModal(true)}
            >
              <Settings className="w-4 h-4 sm:mr-1.5" />
              <span className="hidden sm:inline">연락처 설정</span>
            </Button>
            <Button onClick={() => navigate({ to: '/seeding-campaigns/new' })}>
              <span className="sm:hidden">+ 새 캠페인</span>
              <span className="hidden sm:inline">+ 새 캠페인</span>
            </Button>
          </div>
        </div>
      </div>

      {/* 연락처 설정 미완료 경고 배너 — 캠페인이 있을 때만 표시 */}
      {showContactBanner && (
        <div className="mb-4 flex items-center justify-between gap-3 rounded-lg bg-amber-50 border border-amber-200 px-4 py-3">
          <p className="text-sm text-amber-800">{contactBannerMessage}</p>
          <Button
            variant="outline"
            size="sm"
            className="shrink-0 border-amber-300 text-amber-800 hover:bg-amber-100"
            onClick={() => setShowSettingsModal(true)}
          >
            연락처 설정
          </Button>
        </div>
      )}

      {/* Filters */}
      <div className="mb-6 flex flex-col sm:flex-row gap-3 sm:gap-4">
        <div className="flex-1 sm:max-w-md">
          <Input
            type="text"
            placeholder="캠페인명, 상품명으로 검색..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full"
          />
        </div>
        <Select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <SelectOption value="">전체 상태</SelectOption>
          <SelectOption value="draft">작성중</SelectOption>
          <SelectOption value="scheduled">모집예정</SelectOption>
          <SelectOption value="recruiting">모집중</SelectOption>
          <SelectOption value="pending_selection">선정 대기</SelectOption>
          <SelectOption value="in_progress">진행중</SelectOption>
          <SelectOption value="completed">종료</SelectOption>
          <SelectOption value="archived">아카이브</SelectOption>
        </Select>
      </div>

      {/* Campaign List */}
      {filteredCampaigns.length === 0 ? (
        <div className="py-16 text-center">
          {searchTerm || statusFilter ? (
            /* 검색/필터 결과 없음 */
            (<p className="text-sm text-on-surface-variant">검색 결과가 없어요.</p>)
          ) : (
            /* 캠페인이 하나도 없을 때 — 온보딩 안내 */
            (<>
              <Gift className="w-10 h-10 text-outline-variant mx-auto mb-4" strokeWidth={1.5} />
              <h3 className="text-base font-semibold text-on-surface mb-2">
                아직 캠페인이 없습니다
              </h3>
              <p className="text-sm text-on-surface-variant mb-3">
                체험단 캠페인을 만들어 인플루언서를 모집하고 리뷰 콘텐츠를 수집해보세요.
              </p>
              <p className="text-xs text-outline-variant mb-6">
                캠페인 생성 → 체험단 모집 → 선정 → 미션 관리
              </p>
              <Button
                onClick={() => navigate({ to: '/seeding-campaigns/new' })}
              >
                새 캠페인 만들기
              </Button>
            </>)
          )}
        </div>
      ) : (
        <div className="border border-border rounded-lg overflow-hidden bg-surface-lowest divide-y divide-border">
          {filteredCampaigns.map((campaign) => (
            <Link
              key={campaign.id}
              to="/seeding-campaigns/$campaignId"
              params={{ campaignId: campaign.id }}
              className="block px-3 sm:px-4 py-3 hover:bg-surface transition-colors cursor-pointer"
            >
              {/* 데스크톱: 한 줄 가로 배치 */}
              <div className="hidden sm:flex items-center gap-4">
                <div className="w-12 h-12 flex-shrink-0 bg-surface-low rounded-md overflow-hidden">
                  {(campaign.thumbnail_url || campaign.product_image_url) ? (
                    <img src={campaign.thumbnail_url || campaign.product_image_url} alt={campaign.title} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-outline-variant text-sm">📷</div>
                  )}
                </div>
                <div className="flex-shrink-0 w-20 text-center">
                  <Badge variant={statusBadgeVariant[campaign.status]}>{statusLabel[campaign.status]}</Badge>
                </div>
                <div className="flex-1 min-w-0">
                  <span className="font-medium text-on-surface truncate block">{campaign.title}</span>
                  {/* 임시 저장 캠페인은 상품·정원이 비어 있을 수 있다 (모바일 카드도 동일 처리) */}
                  <p className="text-xs text-outline-variant truncate">{campaign.product_name || '상품 미선택'}</p>
                </div>
                <div className="flex-shrink-0 flex items-center gap-4 text-sm text-on-surface-variant">
                  <div className="text-center">
                    <div className="font-medium text-on-surface">{campaign.application_count}</div>
                    <div className="text-xs">신청</div>
                  </div>
                  <div className="text-center">
                    <div className="font-medium text-on-surface">{campaign.selected_count}</div>
                    <div className="text-xs">선정</div>
                  </div>
                  <div className="text-center">
                    <div className="font-medium text-on-surface">{campaign.recruit_count ?? '-'}</div>
                    <div className="text-xs">정원</div>
                  </div>
                </div>
              </div>

              {/* 모바일: 카드형 레이아웃 */}
              <div className="sm:hidden">
                <div className="flex gap-3">
                  <div className="w-11 h-11 flex-shrink-0 bg-surface-low rounded-md overflow-hidden">
                    {(campaign.thumbnail_url || campaign.product_image_url) ? (
                      <img src={campaign.thumbnail_url || campaign.product_image_url} alt={campaign.title} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-outline-variant text-xs">📷</div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-on-surface truncate">{campaign.title}</span>
                      <Badge variant={statusBadgeVariant[campaign.status]} className="shrink-0">{statusLabel[campaign.status]}</Badge>
                    </div>
                    <p className="text-xs text-outline-variant truncate mt-0.5">{campaign.product_name || '상품 미선택'}</p>
                  </div>
                </div>
                {/* 통계 — 한 줄 텍스트 */}
                <div className="flex gap-3 mt-2 ml-14 text-xs text-on-surface-variant">
                  <span>신청 <strong className="text-on-surface">{campaign.application_count}</strong></span>
                  <span className="text-border">·</span>
                  <span>선정 <strong className="text-on-surface">{campaign.selected_count}</strong></span>
                  <span className="text-border">·</span>
                  <span>정원 <strong className="text-on-surface">{campaign.recruit_count ?? '-'}</strong></span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}

      {/* Count */}
      {filteredCampaigns.length > 0 && (
        <div className="mt-8 flex justify-center">
          <p className="text-sm text-on-surface-variant">
            {filteredCampaigns.length}개 캠페인 표시 중
          </p>
        </div>
      )}
      </div>
      {/* 연락처 설정 모달 */}
      {showSettingsModal && (
        <SeedingSettingsModal onClose={() => setShowSettingsModal(false)} />
      )}
    </>
  )
}
